// ETags (browserdav-nephele-parity Phase 2; content mode since 2026-10-28).
//
// Two modes, the same split as `@nephele/adapter-file-system` with
// localWebDAV's `contentEtagMaxBytes` (parity decision 2 — follow the
// server):
//
//   content mode   a file no larger than CONTENT_ETAG_MAX_BYTES hashes its
//                  bytes with CRC-32 (IEEE), 8 hex digits big-endian —
//                  byte-for-byte Nephele's content ETag — so identical bytes
//                  carry one validator across rewrites, COPYs and servers of
//                  either kind. CRC-32, not CRC-32C: the adapter asks
//                  cyclic-32 for a Castagnoli stream, but that library's
//                  `createHash()` ignores its options and hashes with the
//                  default table (measured 2026-10-28: 'hello etag' →
//                  344f8057 = zlib.crc32). The server as deployed is the
//                  reference, so this follows it; if nephele or cyclic-32
//                  ever fix that, both sides change together.
//   metadata mode  larger files hash size + lastModified with CRC32C
//                  (Castagnoli, as before). The File System
//                  Access API exposes no birthtime, so this can never equal
//                  Nephele's metadata ETag (which bakes birthtime in);
//                  deliberately not chased. Stable across reads, changed by
//                  writes, is what matters there.
//
// The content hash is kept per path against size + lastModified, bounded,
// so a PROPFIND re-reads only members that changed. The server keys on the
// inode too, which a staged PUT always renews; a File has no inode, and two
// same-size writes can land in one millisecond, so the engine forgets a
// path's entry on every write it makes (forgetContentEtags, called from
// FileOperations). A write made outside the engine — the user editing the
// shared directory — is seen through size + lastModified alone. Without a
// path the hash is computed and not kept.

export const CONTENT_ETAG_MAX_BYTES = 128 * 1024

const MAX_ENTRIES = 20000
const cache = new Map() // path → { etag, size, lastModified }

export const contentEtagCache = {
    hits: 0,
    misses: 0,
    get size() {
        return cache.size
    },
    clear() {
        cache.clear()
        this.hits = 0
        this.misses = 0
    },
}

const makeTable = (poly) => {
    const table = new Int32Array(256)
    for (let n = 0; n < 256; n++) {
        let c = n
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? poly ^ (c >>> 1) : c >>> 1
        }
        table[n] = c
    }
    return table
}
const CRC32C_TABLE = makeTable(0x82f63b78) // Castagnoli
const CRC32_TABLE = makeTable(0xedb88320) // IEEE 802.3 (zlib)

const crc = (table, input) => {
    const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input
    let c = 0xffffffff
    for (let i = 0; i < bytes.length; i++) {
        c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
    }
    return (c ^ 0xffffffff) >>> 0
}

/** CRC-32C (Castagnoli) of a string (UTF-8) or a Uint8Array, unsigned. */
export function crc32c(input) {
    return crc(CRC32C_TABLE, input)
}

/** CRC-32 (IEEE, zlib's) of a string (UTF-8) or a Uint8Array, unsigned. */
export function crc32(input) {
    return crc(CRC32_TABLE, input)
}

/**
 * ETag for a File, unquoted hex. Content mode (CRC-32 of the bytes, 8 hex
 * digits) up to CONTENT_ETAG_MAX_BYTES, metadata mode above it. `path` is
 * the cache key for content mode; pass it wherever the caller has one.
 */
export async function fileEtag(file, path = null) {
    if (file.size > CONTENT_ETAG_MAX_BYTES) {
        return crc32c(`${file.size}-${file.lastModified}`).toString(16)
    }
    const hit = path != null ? cache.get(path) : undefined
    if (hit && hit.size === file.size && hit.lastModified === file.lastModified) {
        contentEtagCache.hits++
        return hit.etag
    }
    contentEtagCache.misses++
    const etag = crc32(new Uint8Array(await file.arrayBuffer())).toString(16).padStart(8, '0')
    if (path != null) {
        if (cache.size >= MAX_ENTRIES) cache.delete(cache.keys().next().value)
        cache.set(path, { etag, size: file.size, lastModified: file.lastModified })
    }
    return etag
}

/**
 * Forget the kept hash at `path` and under it (a collection's members),
 * after any write the engine makes there. Cheap: a scan of a small map.
 */
export function forgetContentEtags(path) {
    if (path == null) return
    const prefix = path.endsWith('/') ? path : path + '/'
    for (const key of cache.keys()) {
        if (key === path || key.startsWith(prefix)) cache.delete(key)
    }
}

/**
 * Directory ETag from the listing snapshot (names + sizes + mtimes), so
 * 304-polling a write-box detects added/removed/renamed entries like
 * Nephele's inode-stat ETag does. Slightly stronger than Nephele's: an
 * in-place child content edit changes size/mtime and therefore this ETag
 * too, which Nephele's inode stat misses — an acceptable direction of
 * divergence (pollers see more changes, never fewer).
 */
export async function directoryEtag(dirHandle) {
    const lines = []
    for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
            try {
                const file = await entry.getFile()
                lines.push(`${entry.name}:${file.size}:${file.lastModified}`)
            } catch {
                lines.push(`${entry.name}:unreadable`)
            }
        } else {
            lines.push(`${entry.name}:dir`)
        }
    }
    lines.sort()
    return crc32c(lines.join('\n')).toString(16)
}
